import React, {
    PropsWithChildren,
    useCallback,
    useMemo,
    useState,
} from "react";

//context
import PermissionContext, { IUserPayload } from "./PermissionContext";
import { AccessQuery, isUserAuthorized } from "./core/access";

const PermissionProvider = ({ children }: PropsWithChildren) => {
    const [user, setUserState] = useState<IUserPayload | null>(null);

    const setUser = useCallback((newUser: IUserPayload) => {
        setUserState(newUser);
    }, []);

    const clearUser = useCallback(() => {
        setUserState(null);
    }, []);

    const can = useCallback(
        (query: AccessQuery): boolean => isUserAuthorized(user, query),
        [user]
    );

    const isAuthorized = useCallback(
        (
            roleNames?: string[] | string,
            permissionNames?: string[] | string,
            options?: Pick<AccessQuery, "requireAll" | "check">
        ): Promise<boolean> =>
            Promise.resolve(
                isUserAuthorized(user, {
                    roles: roleNames,
                    permissions: permissionNames,
                    ...options,
                })
            ),
        [user]
    );

    const value = useMemo(
        () => ({
            user,
            setUser,
            clearUser,
            can,
            isAuthorized,
            isLoading: false,
        }),
        [user, setUser, clearUser, can, isAuthorized]
    );

    return (
        <PermissionContext.Provider value={value}>
            {children}
        </PermissionContext.Provider>
    );
};

export default PermissionProvider;